import {Injectable, NgZone} from "@angular/core";
import {HttpService, QueryModel} from "../http/http.service";
import {HttpClient} from "@angular/common/http";
import {map, Observable, Subject} from "rxjs";
import {catchError} from "rxjs/operators";
import {Router} from "@angular/router";
import {UserModel} from '../../models/user/user.model';
import {AuthenticationModel} from '../../models/authentication/authentication.model';
import {BrowserStorageService} from '../browser-storage/browser-storage.service';
import {RegistrationModel} from '../../models/registration/registration.model';
import {InfoMessageService} from '../infomessage/infomessage.service';
import {ChangePasswordModel} from '../../models/change-password/change-password.model';
import {PasswordResetModel} from '../../models/password-reset/password-reset.model';

@Injectable({providedIn: 'root'})
export class AuthenticationService extends HttpService {
    userSubject: Subject<UserModel> = new Subject<UserModel>;
    authConfig: AuthenticationModel = {
        accessToken: '',
        refreshToken: ''
    };
    tokenRefreshTimeout: any = null;
    expiresAt: number = 0;
    roles: string[] = [];
    loggedIn: Subject<boolean> = new Subject<boolean>();
    userUuid: string = '';
    user: UserModel = {
        email: '',
        firstName: '',
        id: '',
        lastName: '',
        mobileNumber: '',
        phoneNumber: '',
        role: '',
        skype: '',
        title: ''
    };

    constructor(protected override http: HttpClient,
                private ngZone: NgZone,
                private router: Router,
                private browserStorageService: BrowserStorageService,
                private infoMessageService: InfoMessageService
    ) {
        super(http);
        this.init();
    }

    init() {
        this.decodeToken();
    }

    isAuthenticated(): boolean | undefined {
        if (this.browserStorageService.hasObject('AuthConfig')) {
            const currentTime = Date.now();
            const expiresAt = this.expiresAt;
            if (currentTime > expiresAt) {
                this.browserStorageService.remove('AuthConfig');
                this.logout();
            }
        }
        return this.browserStorageService.hasObject('AuthConfig');
    }

    getToken(): string {
        return this.authConfig.accessToken ? this.authConfig.accessToken : '';
    }

    getUserId(): string {
        return this.userUuid;
    }

    hasRole(role: string[]): boolean {
        return role.some(r => this.roles.includes(r));
    }

    getRoles(): string[] {
        return this.roles;
    }

    getRefreshToken(): string {
        return this.authConfig.refreshToken ? this.authConfig.refreshToken : '';
    }

    login(credentials: any, isBooking: boolean = false): Observable<boolean> {
        return this.post('auth/login', credentials).pipe(
            map(response => {
                if (response.data) {
                    this.setSessionData(response.data, true, isBooking);
                    return true;
                } else {
                    // Handle the case where response.success is false
                    // You might want to throw an error or handle it differently
                    throw new Error(response.message.data);
                }
            }),
            catchError(error => {
                // Handle errors if necessary or re-throw them
                throw error;
            })
        );
    }

    logout() {
        const postBody = {
            refreshToken: this.getRefreshToken()
        };
        return this.post('auth/logout', postBody).pipe(
            map(response => {
                this.deleteStorage();
                this.user = {
                    email: '',
                    firstName: '',
                    id: '',
                    lastName: '',
                    mobileNumber: '',
                    phoneNumber: '',
                    role: '',
                    skype: '',
                    title: ''
                };
                this.userSubject.next(this.user);
                this.router.navigate(['/home']);
                return true;
            }),
            catchError(error => {
                throw error
            })
        )
    }

    registration(registrationModel: RegistrationModel): Observable<any> {
        return this.post('auth/register', registrationModel).pipe(
            map(response => {
                this.infoMessageService.createMessage('Registrierung erfolgreich', 'success');
                //this.router.navigate(['/anmeldung']);
                // if (response.data) {
                //     this.setSessionData(response.data);
                //     return true;
                // } else {
                //     // Handle the case where response.success is false
                //     // You might want to throw an error or handle it differently
                //     throw new Error(response.message.data);
                // }
            }),
            catchError(error => {
                // Handle errors if necessary or re-throw them
                throw error;
            })
        )
    }

    private setSessionData(loginData: AuthenticationModel, isLogin: boolean = false, isBooking: boolean = false): void {
        this.browserStorageService.set('AuthConfig', loginData);
        this.decodeToken(isLogin, isBooking);
    }


    private decodeToken(isLogin: boolean = false, isBooking: boolean = false) {
        if (this.browserStorageService.hasObject('AuthConfig')) {
            let authConfig = this.browserStorageService.get('AuthConfig');
            if (!authConfig) {
                return;
            } else {
                this.authConfig = JSON.parse(authConfig);
                if (!this.authConfig || !this.authConfig.accessToken || !this.authConfig.refreshToken) {
                    return;
                }

                let decoded = this.parseJWT(this.authConfig.accessToken);
                this.userUuid = decoded.sub;
                this.parseUserData(decoded, isLogin, isBooking);
                this.roles = decoded.realm_access.roles;
                this.expiresAt = decoded.exp * 1000;
                this.loggedIn.next(true);
                this.enableTokenRefresh();
                this.checkIfTokenIsExpired();
            }
        }
    }

    private parseUserData(decoded: any, isLogin: boolean = false, isBooking: boolean = false) {
        this.user.id = decoded.sub;
        this.user.firstName = decoded.given_name;
        this.user.lastName = decoded.family_name;
        this.user.email = decoded.email;
        let rolesToFilterOut = ["offline_access", "default-roles-patriamed", "uma_authorization"];
        let filteredRoles = decoded.realm_access.roles.filter((role: string) => !rolesToFilterOut.includes(role));

        if (filteredRoles.includes('patient')) {
            this.user.role = 'patient';
        } else if (filteredRoles.includes('doctor')) {
            this.user.role = 'doctor';
        } else if (filteredRoles.includes('admin')) {
            this.user.role = 'admin';
        }
        this.userSubject.next(this.user);

        if (isLogin) {
            if (this.user.role === 'patient') {
                if (isBooking) {
                    this.router.navigate(['/profil-uebersicht']);
                } else {
                    this.router.navigate(['/home']);
                }
            }
            if (this.user.role === 'doctor') {
                this.router.navigate(['/doctor-dashboard']);
            }
            if (this.user.role === 'admin') {
                this.router.navigate(['/app-administration']);
            }
        }
    }

    getUser(): UserModel {
        return this.user;
    }

    checkIfTokenIsExpired(): void {
        let now = Date.now();
        let expiresIn = this.expiresAt - now;
        if (expiresIn <= 0 ) {
            this.browserStorageService.remove('AuthConfig');
            this.router.navigate(['home']);
            this.logout().subscribe({
                next: () => {
                },
                error: (error) => {
                }
            });
        }
    }

    deleteStorage(): void {
        this.browserStorageService.remove('AuthConfig');
        this.expiresAt = 0;
        clearTimeout(this.tokenRefreshTimeout);
    }

    private enableTokenRefresh() {
        let refreshDelay = this.getRefreshDelay();
        if (this.tokenRefreshTimeout !== null) {
            clearTimeout(this.tokenRefreshTimeout);
        }
        // Needs to run Outside of the main-zone, as it blocks protractor-specs
        // This disables default Angular change-detection, but the result of this request does not need change detection
        // because each request reads the token from the storage by itself
        this.ngZone.runOutsideAngular(() => {
            this.tokenRefreshTimeout = window.setTimeout(() => {
                this.tokenRefreshTimeout = null;
                this.refreshToken().subscribe(() => {

                    },
                    () => {
                        this.logout().subscribe({
                            next: () => {
                            },
                            error: (error) => {
                            }
                        });
                    }
                );
            }, refreshDelay);
        });
    }

    private getRefreshDelay(): number {
        let milliSecondsBefore = 60 * 1000;
        let now = Date.now();
        let expiresIn = this.expiresAt - now;

        if (expiresIn <= milliSecondsBefore) {
            return 60 * 1000;
        }

        return expiresIn - milliSecondsBefore;
    }


    private parseJWT(token: string) {
        if (token) {
            let base64Url = token.split('.')[1];
            if (base64Url) {
                let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                let jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));
                return JSON.parse(jsonPayload);
            } else {
                // Handle case where token doesn't have expected structure
                return null; // Or handle it according to your application's logic
            }
        } else {
            // Handle case where token is undefined
            return null; // Or handle it according to your application's logic
        }
    }

    refreshToken(): Observable<any> {
        const refreshToken = this.getRefreshToken();
        const queryModel: QueryModel = {
            refreshToken: refreshToken
        };
        return this.get('auth/refresh', queryModel).pipe(
            map(
                (response: any) => {
                    if (response.data) {
                        this.setSessionData(response.data);
                    }
                }),
            catchError(error => {
                // Handle errors if necessary or re-throw them
                throw error;
            })
        );
    }

    changePassword(changePasswordModel: ChangePasswordModel): Observable<any> {
        return this.put('auth/change-password', changePasswordModel).pipe(
            map(
                (response: any) => {
                    return response;
                }),
            catchError(error => {
                // Handle errors if necessary or re-throw them
                throw error;
            })
        );
    }

    sendPasswordForgottenMail(email: string): Observable<any> {
        const queryModel: QueryModel = {
            email: email
        };
        return this.put('auth/request-password-reset', {}, queryModel).pipe(
            map(
                (response: any) => {
                    return response;
                }),
            catchError(error => {
                // Handle errors if necessary or re-throw them
                throw error;
            })
        );
    }

    resetPassword(resetModel: PasswordResetModel): Observable<any> {
        return this.put('auth/reset-password', resetModel).pipe(
            map(
                (response: any) => {
                    return response;
                }),
            catchError(error => {
                // Handle errors if necessary or re-throw them
                throw error;
            })
        );
    }

    confirmMail(key: string): Observable<any> {
        return this.put('auth/confirm-mail', {key: key}).pipe(
            map(
                (response: any) => {
                    return response;
                }),
            catchError(error => {
                // Handle errors if necessary or re-throw them
                throw error;
            })
        );
    }

    confirmRegistration(registerToken: string): Observable<any> {
        return this.get(`auth/register/${registerToken}/validate`).pipe(
            map(
                (response: any) => {
                    return response;
                }),
            catchError(error => {
                // Handle errors if necessary or re-throw them
                throw error;
            })
        );
    }

    resendSuccessLink(query: QueryModel): Observable<any> {
        return this.get(`auth/register/validate/resend-link`, query).pipe(
            map(
                (response: any) => {
                    return response;
                }),
            catchError(error => {
                // Handle errors if necessary or re-throw them
                throw error;
            })
        )
    }
}
